import type { ConversionResult, SubtitleCue } from "@capcut-srt/core";
import { useLocale } from "../providers/LocaleProvider";
import { Metric } from "./Metric";
import { Tooltip } from "./Tooltip";

type AppStatus = "idle" | "processing" | "success" | "empty" | "error";

export interface StatusPanelProps {
  /** Current application status */
  status: AppStatus;
  /** Conversion result (available in success/empty states) */
  result?: ConversionResult;
  /** Preview cues to display (first N cues) */
  preview: SubtitleCue[];
  /** Error message when status is "error" */
  errorMessage?: string;
  /** Called when user clicks the SRT download button */
  onDownloadSrt: () => void;
  /** Called when user clicks the TXT download button */
  onDownloadTxt: () => void;
  /** Called when user clicks the TXT+Time download button */
  onDownloadTxtTime: () => void;
}

/**
 * Status panel component showing conversion results, metrics, preview list,
 * and download buttons. All text uses localized strings via `t()`.
 */
export function StatusPanel({
  status,
  result,
  preview,
  errorMessage,
  onDownloadSrt,
  onDownloadTxt,
  onDownloadTxtTime,
}: StatusPanelProps) {
  const { t } = useLocale();

  const activeSources = result?.sources.filter((s) => s.subtitleCount > 0).length ?? 0;

  return (
    <section className="status-panel" aria-live="polite">
      <div className="stats-grid">
        <Metric
          label={t("status.subtitles")}
          value={result?.subtitleCount.toString() ?? "0"}
        />
        <Metric
          label={t("status.sources")}
          value={activeSources.toString()}
        />
        <Metric
          label={t("status.warnings")}
          value={result?.warnings.length.toString() ?? "0"}
        />
      </div>

      {status === "error" && errorMessage ? (
        <div className="alert">{errorMessage}</div>
      ) : null}

      {status === "empty" ? (
        <div className="alert">{t("error.noSubtitles")}</div>
      ) : null}

      {result && result.subtitleCount > 0 ? (
        <>
          <div className="panel-header">
            <h2>{t("status.preview")}</h2>
            <div className="download-group">
              <Tooltip content={t("download.tooltipSrt")}>
                <button
                  className="download-button"
                  onClick={onDownloadSrt}
                  type="button"
                >
                  {t("download.srt")}
                </button>
              </Tooltip>
              <Tooltip content={t("download.tooltipTxt")}>
                <button
                  className="secondary-action"
                  onClick={onDownloadTxt}
                  type="button"
                >
                  {t("download.txt")}
                </button>
              </Tooltip>
              <Tooltip content={t("download.tooltipTxtTime")}>
                <button
                  className="secondary-action"
                  onClick={onDownloadTxtTime}
                  type="button"
                >
                  {t("download.txtTime")}
                </button>
              </Tooltip>
            </div>
          </div>
          <ol className="preview-list">
            {preview.map((cue) => (
              <li key={`${cue.index}-${cue.startUs}`}>
                <span>{cue.index}</span>
                <div>
                  <time>{formatPreviewRange(cue.startUs, cue.endUs)}</time>
                  <p>{cue.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <div className="empty-preview">
          <h2>{t("status.noFile")}</h2>
          <p>
            {status === "empty"
              ? t("status.noSubtitlesDesc")
              : t("status.noFileDesc")}
          </p>
        </div>
      )}
    </section>
  );
}

function formatPreviewRange(startUs: number, endUs: number): string {
  const start = secondsLabel(startUs);
  const end = secondsLabel(endUs);
  return `${start} - ${end}`;
}

function secondsLabel(microseconds: number): string {
  return `${(microseconds / 1_000_000).toFixed(3)}s`;
}
